/**
 * useInterval Hook
 * A declarative interval hook following Josh Comeau's pattern.
 * Uses refs to avoid stale closures and properly handles cleanup.
 *
 * @see https://overreacted.io/making-setinterval-declarative-with-react-hooks/
 */

import { useEffect, useRef } from 'react';

/**
 * Sets up an interval that calls the callback at the specified delay.
 * The callback is always up-to-date (no stale closure issues).
 *
 * @param callback - Function to call on each interval tick
 * @param delay - Delay in milliseconds, or null to pause the interval
 *
 * @example
 * // Update every second
 * useInterval(() => {
 *   setCount(count + 1);
 * }, 1000);
 *
 * @example
 * // Pause interval when isRunning is false
 * useInterval(() => {
 *   tick();
 * }, isRunning ? 1000 : null);
 */
export function useInterval(
  callback: () => void,
  delay: number | null
): void {
  const savedCallback = useRef<() => void>(callback);

  // Remember the latest callback
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval
  useEffect(() => {
    // Don't schedule if delay is null
    if (delay === null) {
      return;
    }

    const tick = () => {
      savedCallback.current();
    };

    const id = setInterval(tick, delay);

    // Cleanup on unmount or delay change
    return () => clearInterval(id);
  }, [delay]);
}

export default useInterval;
